import React from 'react';
import { Dayjs } from 'dayjs';
import { styled, Box, FormHelperText } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import Button from '../button';

const StyledDataPicker = styled(MuiDatePicker)(() => ({
  '& .MuiInputBase-root': {
    padding: 0,
    width: '130px',
    '& .MuiInputBase-input': { padding: '10px 16px' },
    '& .MuiInputAdornment-root': {
      display: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--neutral-700)',
      borderRadius: '6px',
      boxShadow: 'var(--shadow-xsm)',
    },
  },

  '& .Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--blue-500)',
    },
  },
}));

export interface CustomDatePickerProps extends DatePickerProps<Dayjs> {
  selectToday: () => void;
  closeAction: () => void;
  showErrorMsg?: boolean;
  errorMessage?: string;
}

const DatePicker = React.forwardRef<HTMLInputElement, CustomDatePickerProps>(
  (props, ref) => {
    const {
      showErrorMsg,
      selectToday,
      errorMessage,
      closeAction,
      ...otherProps
    } = props;

    const hasError = Boolean(errorMessage);
    const errorStyle = hasError
      ? { border: '1px solid var(--danger-500)', borderRadius: '6px' }
      : null;

    return (
      <Box>
        <StyledDataPicker
          sx={errorStyle}
          ref={ref}
          {...otherProps}
          showDaysOutsideCurrentMonth
          slots={{
            toolbar: () => (
              <Box
                className="date-picker-actions"
                display="flex"
                gap={2}
                padding="16px 24px"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ flexBasis: '50%' }}
                  onClick={selectToday}
                >
                  오늘 날짜
                </Button>
                <Button
                  variant="contained"
                  sx={{ flexBasis: '50%' }}
                  onClick={closeAction}
                >
                  선택하기
                </Button>
              </Box>
            ),
          }}
          slotProps={{
            desktopPaper: {
              sx: {
                boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
                borderRadius: '6px',
                border: '1px solid var(--neutral-700)',
              },
            },
            layout: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                '& .date-picker-actions': {
                  order: 2,
                },
                '& .MuiButtonBase-root': {
                  '& .Mui-disabled': {
                    color: 'red',
                    backgroundColor: 'black'
                  }
                },
                '& .MuiDateCalendar-root': {
                  padding: '24px',
                  gap: '16px',
                  '& .MuiPickersFadeTransitionGroup-root': {
                    '& .MuiDayCalendar-root': {
                      '& .MuiDayCalendar-header': {
                        '& .MuiDayCalendar-weekDayLabel': {
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '-0.1px',
                          fontWeight: 500,
                          color: 'var(--neutral-800)',
                        },
                      },
                      '& .MuiPickersSlideTransition-root': {
                        minHeight: '200px',
                        '& .MuiDayCalendar-monthContainer': {
                          '& .MuiDayCalendar-weekContainer': {
                            '& .MuiPickersDay-root': {
                              fontSize: '14px',
                              lineHeight: '20px',
                              letterSpacing: '-0.1px',
                              fontWeight: 500,
                              color: 'var(--gray-700)',
                              '&:hover': {
                                color: 'var(--blue-gradient-03)',
                                backgroundColor: 'transparent',
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'var(--blue-25)',
                                color: 'var(--blue-gradient-03)',
                                width: '36px',
                                height: '36px',
                                borderRadius: '5px',
                              },
                              '&.MuiPickersDay-dayOutsideMonth': {
                                color: 'var(--neutral-700)',
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
            calendarHeader: {
              sx: {
                position: 'relative',
                margin: 'unset',
                padding: 'unset',
                '& .MuiPickersCalendarHeader-labelContainer': {
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                },
                '& .MuiPickersArrowSwitcher-root': {
                  width: '100%',
                  justifyContent: 'space-between',
                  '& .MuiButtonBase-root': {
                    border: '1px solid var(--neutral-700)',
                    borderRadius: '5px',
                    padding: '10px',
                    width: '40px',
                    height: '40px',
                    boxShadow: 'var(--shadow-xsm)',
                  },
                },
              },
            },
          }}
        />
        {showErrorMsg && (
          <FormHelperText
            sx={{
              width: '100%',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '-0.1px',
              color: hasError ? 'var(--danger-500)' : 'var(--gray-50)',
              margin: 'unset',
            }}
          >
            {hasError ? errorMessage : ''}
          </FormHelperText>
        )}
      </Box>
    );
  },
);

export default DatePicker;
